import { useEffect, useRef, useState } from 'react';
import { useMessagingChannels } from '../../hooks/useMessagingChannels';
import { isMessagingChannelOpen } from '../../utils/messagingChannels';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Button, FormField, Modal } from '@/components';
import { DashboardPage } from '../../components/PremiumDesign';
import { TemplatePhonePreview } from '../../components/TemplatePhonePreview';
import { EmailTemplatePreview } from '../../components/EmailTemplatePreview';
import { parseApiError } from '@/common/utils';
import { TemplateScheduleOptions } from '../../template-schedules';
import {
  previewReminderImport,
  confirmReminderImport,
  getReminderImportSampleTemplate,
} from '../api';
import {
  CONFLICT_CONTACT_REQUIRED_COPY,
  CONFLICT_RESOLUTION_REQUIRED_COPY,
  PREVIEW_EXPIRED_COPY,
  buildReminderImportConfirmPayload,
  downloadReminderImportTemplate,
  formatDateTimeOffset,
  getDuplicateRowKey,
  getImportTemplateFileName,
  getSchemaVariableHeaders,
  getSelectedDuplicatePhones,
  schemaForSendingMode,
  triggerBlobDownload,
  validateReminderImportFile,
} from '../utils';
import {
  reminderKeys,
  reminderImportKeys,
  useReminderImportJobs,
  useReminderImportSchema,
} from '../hooks';
import { ReminderWizardSteps } from './ReminderWizardSteps';
import { ReminderImportPreviewStep } from './ReminderImportPreviewStep';
import { ReminderImportTemplateStep } from './ReminderImportTemplateStep';

function FileDocIcon({ className = 'w-5 h-5' }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="17" x2="16" y2="17" />
    </svg>
  );
}

function ImportPhonePreviewPanel({ template, loading }) {
  return (
    <div className="reminders-phone-panel w-full lg:w-[388px] shrink-0 self-start flex flex-col items-center rounded-2xl border border-surface-mint bg-form-background-light p-3 sm:p-4">
      <div className="reminders-phone-slot">
        {loading ? (
          <div className="w-[340px] h-[640px] rounded-[34px] border-[8px] border-t-[10px] border-b-[10px] border-phone-bezel bg-white relative shadow-xl flex flex-col overflow-hidden">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-4 bg-phone-bezel rounded-b-[13px] z-50" />
            <div className="h-6 w-full bg-white shrink-0" />
            <div className="bg-white px-3 py-2 flex items-center gap-2 border-b border-[#E5E5E5] shrink-0">
              <div className="h-7 w-7 rounded-full bg-[#E8E8E8] animate-pulse shrink-0" />
              <div className="flex flex-col gap-1.5 min-w-0">
                <div className="h-2.5 w-28 rounded bg-[#E8E8E8] animate-pulse" />
                <div className="h-2 w-12 rounded bg-[#F0F0F0] animate-pulse" />
              </div>
            </div>
            <div className="flex-1 bg-[#F7F8FA] p-3 flex flex-col gap-2 min-h-0">
              <div className="self-center h-4 w-36 rounded bg-[#FEF6D8] animate-pulse" />
              <div className="self-center h-4 w-14 rounded bg-white border border-[#E5E5E5] animate-pulse" />
              <div className="bg-white border border-[#E5E5E5] rounded-xl rounded-tl-none shadow-sm p-2.5 max-w-[92%] flex flex-col gap-2">
                <div className="h-3 w-40 rounded bg-[#E8E8E8] animate-pulse" />
                <div className="h-2.5 w-full rounded bg-[#F0F0F0] animate-pulse" />
                <div className="h-2.5 w-[90%] rounded bg-[#F0F0F0] animate-pulse" />
                <div className="h-2.5 w-3/4 rounded bg-[#F0F0F0] animate-pulse" />
                <div className="h-2.5 w-full rounded bg-[#F0F0F0] animate-pulse" />
                <div className="h-2.5 w-2/3 rounded bg-[#F0F0F0] animate-pulse" />
              </div>
            </div>
            <div className="bg-[#F7F8FA] px-3 pb-3 shrink-0">
              <div className="h-9 w-full rounded-full bg-white border border-[#E5E5E5] animate-pulse" />
            </div>
          </div>
        ) : (
          <TemplatePhonePreview
            key={template?.id || 'empty'}
            template={template}
            message={template ? '' : 'Select a template to preview the message'}
          />
        )}
      </div>
    </div>
  );
}

function ImportEmailPreviewPanel({ template, subject }) {
  return (
    <div className="w-full lg:w-[320px] xl:w-[350px] 2xl:w-[370px] shrink-0 flex flex-col items-center justify-center rounded-2xl border border-surface-mint bg-form-background-light p-3 sm:p-4 my-auto">
      <EmailTemplatePreview
        template={template}
        subject={subject || template?.subject || ''}
        headerTitle="Email Reminder Preview"
        className="w-full"
      />
    </div>
  );
}

function hasDuplicateConflicts(previewData) {
  return Boolean(
    previewData?.duplicates?.length || previewData?.duplicates_count,
  );
}

function needsSkipContactPick(duplicateAction) {
  return duplicateAction === 'skip';
}

function lastSuccessfulJobId(jobs, template) {
  const templateId = template?.id != null ? String(template.id) : '';
  const templateName = template?.name || template?.template_name;
  const match = jobs.find((job) => {
    if (job.status !== 'completed') return false;
    if (
      templateId &&
      job.template_id &&
      String(job.template_id) === templateId
    ) {
      return true;
    }
    return Boolean(templateName && job.template_name === templateName);
  });
  return match ? String(match.id) : '';
}

function isPreviewExpiredError(err, message) {
  const status = err?.response?.status;
  const lower = (message || '').toLowerCase();
  return (
    status === 400 &&
    (lower.includes('token') ||
      lower.includes('expired') ||
      lower.includes('preview'))
  );
}

export function ReminderImportWizard({
  onClose,
  companyId,
  channel = 'whatsapp',
}) {
  const queryClient = useQueryClient();
  const integration = useMessagingChannels(companyId);
  const channelOpen =
    integration.isReady && isMessagingChannelOpen(integration, channel);
  const downloadedKeyRef = useRef('');
  const scheduleOptionsRef = useRef(null);

  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaBase64, setMediaBase64] = useState(null);
  const [cardImages, setCardImages] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [schemaMeta, setSchemaMeta] = useState(null);
  const [hasDownloaded, setHasDownloaded] = useState(false);
  const [previousJobId, setPreviousJobId] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [duplicateAction, setDuplicateAction] = useState('');
  const [duplicateActionError, setDuplicateActionError] = useState(false);
  const [selectedDuplicateKeys, setSelectedDuplicateKeys] = useState([]);
  const [duplicateContactError, setDuplicateContactError] = useState(false);
  const [missingAction, setMissingAction] = useState('keep_missing');
  const [customSubject, setCustomSubject] = useState('');
  const [sendOnContactDate, setSendOnContactDate] = useState(false);
  const [standardSendingMode, setStandardSendingMode] = useState('scheduled');

  const templateName =
    selectedTemplate?.name ||
    selectedTemplate?.template_name ||
    (channel === 'email' ? 'email_reminder' : '');

  const isMediaRequired =
    selectedTemplate?.components?.some(
      (c) =>
        c.type === 'HEADER' &&
        ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(c.format?.toUpperCase()),
    ) || false;

  const resetWizard = () => {
    setStep(0);
    setFile(null);
    setMediaFile(null);
    setMediaBase64(null);
    setCardImages([]);
    setTemplateId('');
    setSelectedTemplate(null);
    setSchemaMeta(null);
    setHasDownloaded(false);
    setPreviousJobId('');
    setPreviewData(null);
    setPreviewOpen(false);
    setDuplicateAction('');
    setDuplicateActionError(false);
    setSelectedDuplicateKeys([]);
    setDuplicateContactError(false);
    setMissingAction('keep_missing');
    setSendOnContactDate(false);
    setStandardSendingMode('scheduled');
    downloadedKeyRef.current = '';
  };

  const handleClose = () => {
    resetWizard();
    onClose();
  };

  const { data: jobsData } = useReminderImportJobs(companyId, {
    enabled: false,
  });
  const sendingMode =
    channel === 'whatsapp' && sendOnContactDate
      ? 'yearly_date'
      : 'schedule_once';

  const {
    data: schema,
    isPending: isSchemaLoading,
    isError: isSchemaError,
    isFetching: isSchemaFetching,
  } = useReminderImportSchema(companyId, {
    templateId: templateId || undefined,
    templateName: templateName || undefined,
    channel,
    sendingMode: channel === 'whatsapp' ? sendingMode : undefined,
  });

  const jobs = Array.isArray(jobsData) ? jobsData : jobsData?.results || [];

  const modeSchema =
    channel === 'whatsapp' ? schemaForSendingMode(schema, sendingMode) : schema;
  const columns = modeSchema?.columns || [];
  const requiredHeaders = columns
    .filter((col) => col.required)
    .map((col) => col.header);

  const isCarousel = Boolean(schema?.is_carousel);
  const carouselCards = schema?.carousel_cards || [];

  const displayTemplateName =
    selectedTemplate?.name ||
    selectedTemplate?.template_name ||
    schemaMeta?.template_name ||
    templateName ||
    (channel === 'email' ? 'Email Reminder' : 'WhatsApp Template');

  useEffect(() => {
    if (!schema) return;
    if (
      templateId &&
      schema.template_id &&
      String(schema.template_id) !== String(templateId)
    ) {
      return;
    }
    if (
      channel === 'whatsapp' &&
      schema.sending_mode &&
      schema.sending_mode !== sendingMode
    ) {
      return;
    }
    setSchemaMeta({
      template_id: schema.template_id || selectedTemplate?.id || templateId,
      template_name:
        schema.template_name ||
        selectedTemplate?.name ||
        selectedTemplate?.template_name ||
        (channel === 'email' ? 'email_reminder' : ''),
      template_language:
        schema.template_language || selectedTemplate?.language || 'en_US',
    });
    if (channel === 'email') {
      setCustomSubject(schema.subject || selectedTemplate?.subject || '');
    }
  }, [schema, selectedTemplate, templateId, channel, sendingMode]);

  useEffect(() => {
    if (step > 0 && !templateId) setStep(0);
  }, [step, templateId]);

  useEffect(() => {
    if (step === 3 && !previewData?.preview_token) setStep(2);
  }, [step, previewData]);

  const downloadFromSchema = async () => {
    if (!templateId && !templateName) {
      toast.error('Please select a template.');
      return false;
    }
    if (isSchemaLoading || isSchemaFetching) {
      toast.error('Template columns still loading. Try again.');
      return false;
    }
    if (isSchemaError || !columns.length) {
      toast.error(
        'Could not load template columns. Select the template again.',
      );
      return false;
    }

    try {
      downloadReminderImportTemplate(modeSchema);
      setHasDownloaded(true);
      return true;
    } catch (err) {
      try {
        const blob = await getReminderImportSampleTemplate({
          companyId,
          templateName: modeSchema.template_name || templateName,
          templateVariables: getSchemaVariableHeaders(columns),
          sendingMode: channel === 'whatsapp' ? sendingMode : undefined,
        });
        triggerBlobDownload(blob, getImportTemplateFileName(modeSchema));
        setHasDownloaded(true);
        return true;
      } catch (fallbackErr) {
        toast.error(
          fallbackErr.message ||
            err.message ||
            'Failed to build template file.',
        );
        return false;
      }
    }
  };

  const goBackToUpload = () => {
    setPreviewData(null);
    setPreviewOpen(false);
    setDuplicateAction('');
    setDuplicateActionError(false);
    setSelectedDuplicateKeys([]);
    setDuplicateContactError(false);
    setStep(2);
  };

  const { mutate: runPreview, isPending: isPreviewing } = useMutation({
    mutationFn: previewReminderImport,
    onSuccess: (data) => {
      setPreviewData(data);
      setDuplicateAction('');
      setDuplicateActionError(false);
      setSelectedDuplicateKeys([]);
      setDuplicateContactError(false);
      setPreviewOpen(true);
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to preview import file.');
      if (err?.response?.status === 400) {
        setPreviewData(null);
        setPreviewOpen(false);
        setFile(null);
        setStep(1);
      }
    },
  });

  const { mutate: runConfirm, isPending: isConfirming } = useMutation({
    mutationFn: confirmReminderImport,
    onSuccess: () => {
      toast.success('Reminders imported successfully');
      queryClient.invalidateQueries({ queryKey: reminderKeys.all });
      queryClient.invalidateQueries({ queryKey: ['reminders-summary'] });
      queryClient.invalidateQueries({
        queryKey: reminderImportKeys.list(companyId),
      });
      handleClose();
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      if (isPreviewExpiredError(err, message)) {
        toast.error(PREVIEW_EXPIRED_COPY);
        goBackToUpload();
        return;
      }
      toast.error(message || 'Failed to confirm import.');
    },
  });

  const handleTemplateSelect = (template) => {
    if (!template?.id) return;
    if (String(template.id) === String(templateId)) {
      setSelectedTemplate(template);
      return;
    }
    setSelectedTemplate(template);
    setTemplateId(String(template.id));
    setMediaFile(null);
    setMediaBase64(null);
    setCardImages([]);
    setSchemaMeta(null);
    setHasDownloaded(false);
    setFile(null);
    setPreviewData(null);
    setPreviewOpen(false);
    setDuplicateAction('');
    setDuplicateActionError(false);
    setSelectedDuplicateKeys([]);
    setDuplicateContactError(false);
    setMissingAction('keep_missing');
    setPreviousJobId(lastSuccessfulJobId(jobs, template));
    downloadedKeyRef.current = '';
  };

  const handleFileChange = (nextFile) => {
    const error = nextFile ? validateReminderImportFile(nextFile) : null;
    if (error) {
      toast.error(error);
      setFile(null);
      setPreviewData(null);
      setPreviewOpen(false);
      setSelectedDuplicateKeys([]);
      return;
    }
    setFile(nextFile);
    setPreviewData(null);
    setPreviewOpen(false);
    setSelectedDuplicateKeys([]);
    setDuplicateAction('');
    setDuplicateActionError(false);
    setDuplicateContactError(false);
  };

  const handleMediaChange = (nextFile) => {
    if (!nextFile) {
      setMediaFile(null);
      setMediaBase64(null);
      return;
    }
    if (nextFile.type.startsWith('video/')) {
      if (
        nextFile.type !== 'video/mp4' &&
        !nextFile.name.toLowerCase().endsWith('.mp4')
      ) {
        toast.error('Only MP4 videos are supported.');
        return;
      }
      if (nextFile.size > 16 * 1024 * 1024) {
        toast.error(
          'Video exceeds 16 MB limit. Please upload a smaller H.264 MP4.',
        );
        return;
      }
    } else {
      if (nextFile.size > 15 * 1024 * 1024) {
        toast.error('File size exceeds 15 MB limit.');
        return;
      }
    }
    setMediaFile(nextFile);

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target.result;
      const base64Data = result.split(',')[1];
      setMediaBase64(base64Data);
    };
    reader.readAsDataURL(nextFile);
  };

  const handleCardMediaChange = (index, nextFile, cardMediaType) => {
    if (!nextFile) {
      setCardImages((prev) => {
        const copy = [...prev];
        copy[index] = null;
        return copy;
      });
      return;
    }

    if (cardMediaType === 'VIDEO' || nextFile.type.startsWith('video/')) {
      if (
        nextFile.type !== 'video/mp4' &&
        !nextFile.name.toLowerCase().endsWith('.mp4')
      ) {
        toast.error('Only MP4 videos are supported.');
        return;
      }
      if (nextFile.size > 16 * 1024 * 1024) {
        toast.error(
          'Video exceeds 16 MB limit. Please upload a smaller H.264 MP4.',
        );
        return;
      }
    } else {
      if (nextFile.size > 15 * 1024 * 1024) {
        toast.error('File size exceeds 15 MB limit.');
        return;
      }
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target.result;
      setCardImages((prev) => {
        const copy = [...prev];
        copy[index] = { file: nextFile, dataUrl: result };
        return copy;
      });
    };
    reader.readAsDataURL(nextFile);
  };

  const onPreview = () => {
    if (!channelOpen) {
      toast.error('That channel is not connected.');
      return;
    }
    const fileError = validateReminderImportFile(file);
    if (fileError) {
      toast.error(fileError);
      return;
    }
    if (!templateId && !schemaMeta?.template_id && !schemaMeta?.template_name) {
      toast.error('Please select a template.');
      setStep(0);
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    const id = templateId || schemaMeta?.template_id;
    const name =
      selectedTemplate?.name ||
      selectedTemplate?.template_name ||
      schemaMeta?.template_name ||
      templateName;
    const language =
      selectedTemplate?.language || schemaMeta?.template_language;

    if (id) formData.append('template_id', id);
    else if (name) formData.append('template_name', name);
    if (language) formData.append('template_language', language);
    if (channel === 'whatsapp') formData.append('sending_mode', sendingMode);
    if (previousJobId) formData.append('previous_job_id', previousJobId);
    if (channel) formData.append('channel', channel);

    runPreview({ companyId, formData });
  };

  const closePreviewModal = () => {
    setPreviewOpen(false);
    setPreviewData(null);
    setDuplicateAction('');
    setDuplicateActionError(false);
    setSelectedDuplicateKeys([]);
    setDuplicateContactError(false);
  };

  const handleDuplicateActionChange = (value) => {
    setDuplicateAction(value);
    setDuplicateActionError(false);
    if (value !== 'skip') {
      setSelectedDuplicateKeys([]);
      setDuplicateContactError(false);
    }
  };

  const handleToggleDuplicateRow = (key) => {
    setSelectedDuplicateKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
    setDuplicateContactError(false);
  };

  const handleToggleAllDuplicates = () => {
    const duplicates = previewData?.duplicates || [];
    const allKeys = duplicates.map((row, index) =>
      getDuplicateRowKey(row, index),
    );
    const allSelected =
      allKeys.length > 0 &&
      allKeys.every((key) => selectedDuplicateKeys.includes(key));
    setSelectedDuplicateKeys(allSelected ? [] : allKeys);
    setDuplicateContactError(false);
  };

  const handlePreviewContinue = () => {
    if (!previewData?.valid_rows_count) return;

    const hasConflicts = hasDuplicateConflicts(previewData);
    if (hasConflicts && !duplicateAction) {
      setDuplicateActionError(true);
      toast.error(CONFLICT_RESOLUTION_REQUIRED_COPY);
      return;
    }
    if (
      needsSkipContactPick(duplicateAction) &&
      selectedDuplicateKeys.length === 0
    ) {
      setDuplicateContactError(true);
      toast.error(CONFLICT_CONTACT_REQUIRED_COPY);
      return;
    }

    setDuplicateActionError(false);
    setDuplicateContactError(false);
    setPreviewOpen(false);
    setStep(3);
  };

  const onConfirm = () => {
    if (!channelOpen) {
      toast.error('That channel is not connected.');
      return;
    }
    if (!previewData?.preview_token) {
      toast.error(PREVIEW_EXPIRED_COPY);
      goBackToUpload();
      return;
    }
    if (!previewData.valid_rows_count) {
      toast.error('Nothing to import. Fix the file and upload again.');
      return;
    }
    if (hasDuplicateConflicts(previewData) && !duplicateAction) {
      toast.error(CONFLICT_RESOLUTION_REQUIRED_COPY);
      setDuplicateActionError(true);
      setPreviewOpen(true);
      return;
    }
    if (
      needsSkipContactPick(duplicateAction) &&
      selectedDuplicateKeys.length === 0
    ) {
      toast.error(CONFLICT_CONTACT_REQUIRED_COPY);
      setDuplicateContactError(true);
      setPreviewOpen(true);
      return;
    }

    const scheduleOptions = scheduleOptionsRef.current;
    if (!scheduleOptions) {
      toast.error('Choose a sending mode.');
      return;
    }

    const validation = scheduleOptions.validate();
    if (!validation.ok) {
      toast.error(validation.message);
      return;
    }

    if (isCarousel) {
      if (carouselCards.length > 0) {
        const missingCard = carouselCards.findIndex((c, i) => !cardImages[i]);
        if (missingCard !== -1) {
          toast.error(
            `Please upload a media file for Card ${missingCard + 1}.`,
          );
          return;
        }
      }
    } else if (isMediaRequired && !mediaBase64) {
      toast.error('This template requires a media file attachment.');
      return;
    }

    try {
      const payload = buildReminderImportConfirmPayload({
        previewToken: previewData.preview_token,
        duplicateAction,
        missingAction,
        subject: channel === 'email' ? customSubject : undefined,
        duplicatePhones: needsSkipContactPick(duplicateAction)
          ? getSelectedDuplicatePhones(
              previewData.duplicates,
              selectedDuplicateKeys,
            )
          : undefined,
        scheduleResult: scheduleOptions.getSchedulePayload(),
      });

      payload.channel = channel;

      if (isCarousel) {
        payload.card_images = cardImages.map((ci) => ci.dataUrl);
      } else if (mediaBase64 && mediaFile) {
        payload.document_base64 = mediaBase64;
        payload.document_filename = mediaFile.name;
      }

      runConfirm({ companyId, payload });
    } catch (err) {
      toast.error(err.message || 'Failed to confirm import.');
    }
  };

  const canContinueFromDownload = Boolean(columns.length) && !isSchemaLoading;
  const canSubmitUpload =
    Boolean(file && (templateId || channel === 'email')) && !isPreviewing;
  const validRows = previewData?.valid_rows_count || 0;
  const totalRows = previewData?.total_rows || 0;
  const phonePreviewTemplate = selectedTemplate;

  const footer =
    step === 0
      ? {
          label: 'Continue',
          onClick: () => {
            if (!templateId || !selectedTemplate) {
              toast.error(
                channel === 'email'
                  ? 'Please select an email template.'
                  : 'Please select a WhatsApp template.',
              );
              return;
            }
            setStep(1);
          },
          disabled: !templateId || !selectedTemplate,
        }
      : step === 1
        ? {
            label: 'Continue',
            onClick: () => setStep(2),
            disabled: !canContinueFromDownload,
          }
        : step === 2
          ? {
              label: isPreviewing ? 'Previewing...' : 'Preview',
              onClick: onPreview,
              disabled: !canSubmitUpload,
            }
          : {
              label: isConfirming ? 'Importing...' : 'Confirm',
              onClick: onConfirm,
              disabled:
                isConfirming || validRows === 0 || !previewData?.preview_token,
            };

  return (
    <DashboardPage className="reminders-import !overflow-hidden !h-full flex-1 min-h-0 flex flex-col">
      <div className="flex items-center justify-between gap-2 sm:gap-3 shrink-0 mb-4">
        <div className="min-w-0 overflow-x-auto no-scrollbar py-1">
          <ReminderWizardSteps currentStep={step} />
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {step > 0 && (
            <Button
              variant="secondary"
              onClick={() => setStep((s) => s - 1)}
              className="h-9 sm:h-10 px-3 sm:px-4 text-xs sm:text-sm font-semibold"
            >
              Back
            </Button>
          )}
          <Button
            onClick={footer.onClick}
            disabled={footer.disabled}
            className="h-9 sm:h-10 px-4 sm:px-6 text-xs sm:text-sm font-semibold"
          >
            {footer.label}
          </Button>
        </div>
      </div>

      <div className="flex-1 min-h-0 flex flex-col rounded-2xl bg-layer-5-card premium-surface border border-border-default overflow-hidden">
        <div className="flex flex-col lg:flex-row flex-1 min-h-0 overflow-hidden">
          <div
            className={`flex flex-col gap-4 sm:gap-5 px-4 sm:px-6 py-4 sm:py-5 flex-1 min-w-0 min-h-0 ${
              step === 0
                ? 'overflow-hidden'
                : 'overflow-y-auto custom-scrollbar'
            }`}
          >
            <div
              className={step === 0 ? 'flex flex-col flex-1 min-h-0' : 'hidden'}
            >
              <ReminderImportTemplateStep
                companyId={companyId}
                selectedTemplateId={templateId}
                onSelect={handleTemplateSelect}
                channel={channel}
              />
            </div>

            {step === 1 && (
              <div className="flex flex-col gap-4 sm:gap-5">
                {selectedTemplate && (
                  <div className="flex items-center justify-between gap-3 rounded-xl border border-primary/30 bg-surface-mint px-4 py-3 shrink-0">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold uppercase tracking-wider text-text-secondary">
                        Selected template
                      </p>
                      <p className="text-sm font-semibold text-text-primary truncate">
                        {displayTemplateName}
                      </p>
                      {(schemaMeta?.template_language ||
                        selectedTemplate.language) && (
                        <p className="text-xs text-text-secondary mt-0.5">
                          {schemaMeta?.template_language ||
                            selectedTemplate.language}
                        </p>
                      )}
                    </div>
                    <Button
                      variant="secondary"
                      className="shrink-0"
                      onClick={() => setStep(0)}
                    >
                      Change
                    </Button>
                  </div>
                )}

                {channel === 'email' && (
                  <FormField
                    label="Email Subject"
                    helpText="You can edit the email subject line for this reminder import batch."
                  >
                    <input
                      type="text"
                      value={customSubject}
                      onChange={(e) => setCustomSubject(e.target.value)}
                      placeholder="Enter subject..."
                      className="w-full h-10 px-3 py-2 border border-border-default rounded-lg text-sm text-text-primary bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    />
                  </FormField>
                )}

                <div className="rounded-[10px] border border-border-default bg-[#FAFAFA] px-4 py-3.5">
                  <h4 className="text-sm font-medium text-text-primary mb-2">
                    How to import {displayTemplateName}
                  </h4>
                  {channel === 'email' ? (
                    <ul className="text-[13.5px] leading-relaxed text-text-secondary list-disc pl-4 space-y-1.5">
                      <li>Download the template spreadsheet below.</li>
                      <li>
                        Fill in the recipient&apos;s email, name, and message.
                      </li>
                      <li>
                        <span className="font-medium text-text-primary">
                          Email addresses must be valid
                        </span>{' '}
                        (e.g., user@example.com).
                      </li>
                      <li>
                        If providing a phone number, it{' '}
                        <span className="font-medium text-text-primary">
                          must include a country code
                        </span>{' '}
                        (e.g., +9198...).
                      </li>
                      <li>
                        The reminder send time will be selected in the final
                        Confirm step.
                      </li>
                      <li>
                        Upload the filled spreadsheet back on this screen.
                      </li>
                    </ul>
                  ) : (
                    <ul className="text-[13.5px] leading-relaxed text-text-secondary list-disc pl-4 space-y-1.5">
                      <li>Download the template spreadsheet below.</li>
                      <li>
                        Fill in the recipient&apos;s phone, name, and template
                        variables.
                      </li>
                      <li>
                        <span className="font-medium text-text-primary">
                          Phone numbers must include a country code
                        </span>{' '}
                        (e.g., +9198...).
                      </li>
                      <li>
                        The reminder send time will be selected in the final
                        Confirm step.
                      </li>
                      <li>
                        Upload the filled spreadsheet back on this screen.
                      </li>
                    </ul>
                  )}
                </div>

                {channel === 'whatsapp' && (
                  <div className="flex items-center justify-between gap-3 rounded-xl border border-[#EBF4EA] bg-white px-3 py-2.5">
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-text-primary">
                        Send on each contact’s date
                      </span>
                      <span className="block text-xs text-text-secondary">
                        Use the event_date column in your sheet to send
                        reminders yearly.
                        {sendOnContactDate
                          ? ' Formats: YYYY-MM-DD or DD/MM/YYYY.'
                          : ''}
                      </span>
                    </span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={sendOnContactDate}
                      aria-label="Send on each contact's date"
                      onClick={() => {
                        setSendOnContactDate((on) => !on);
                        setFile(null);
                        setHasDownloaded(false);
                        setPreviewData(null);
                        setPreviewOpen(false);
                        setDuplicateAction('');
                        setDuplicateActionError(false);
                        setSelectedDuplicateKeys([]);
                        setDuplicateContactError(false);
                        downloadedKeyRef.current = '';
                      }}
                      className={`relative h-6 w-11 shrink-0 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
                        sendOnContactDate ? 'bg-primary' : 'bg-[#D0D5D1]'
                      }`}
                    >
                      <span
                        aria-hidden
                        className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
                          sendOnContactDate ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                )}

                {columns.length > 0 && (
                  <div className="flex flex-col gap-2">
                    <p className="text-xs font-semibold uppercase tracking-wider text-text-secondary">
                      Spreadsheet Columns Format
                    </p>
                    <div className="overflow-x-auto rounded-xl border border-border-default bg-white">
                      <table className="w-full text-left text-sm whitespace-nowrap">
                        <thead className="bg-[#F8F9FA]">
                          <tr>
                            {columns.map((col) => (
                              <th
                                key={col.header}
                                className="px-4 py-2.5 font-semibold text-text-primary border-b border-r border-border-default last:border-r-0"
                              >
                                {col.header}
                                {col.required && (
                                  <span
                                    className="text-[#E0435C] ml-0.5"
                                    title="Required"
                                  >
                                    *
                                  </span>
                                )}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            {columns.map((col) => (
                              <td
                                key={col.header}
                                className="px-4 py-2.5 text-text-secondary border-r border-border-default last:border-r-0"
                              >
                                {col.description || '...'}
                              </td>
                            ))}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    {requiredHeaders.length > 0 && (
                      <p className="text-xs text-text-muted">
                        Required columns marked with *
                      </p>
                    )}
                  </div>
                )}

                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-[#F5F5F5] px-3.5 sm:px-4 py-3 sm:py-3.5 rounded-[8px] shrink-0">
                  <p className="text-[13px] sm:text-[14px] leading-[20px] text-text-secondary flex-1 min-w-0">
                    {hasDownloaded
                      ? 'Template file downloaded. Fill it, then continue to upload.'
                      : channel === 'email'
                        ? 'Download the blank spreadsheet for this email template.'
                        : sendOnContactDate
                          ? 'Download includes event_date (YYYY-MM-DD or DD/MM/YYYY). Each contact is sent every year on that date.'
                          : 'Download the blank spreadsheet for this WhatsApp template.'}
                  </p>
                  <Button
                    variant="secondary"
                    onClick={downloadFromSchema}
                    disabled={
                      isSchemaLoading || isSchemaFetching || !columns.length
                    }
                    icon={<FileDocIcon className="w-4 h-4" />}
                    iconPosition="start"
                    className="w-full sm:w-auto shrink-0"
                  >
                    {isSchemaLoading
                      ? 'Loading columns...'
                      : hasDownloaded
                        ? 'Download again'
                        : 'Download Template'}
                  </Button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="flex flex-col gap-4 sm:gap-5">
                <p className="text-sm text-text-secondary">
                  {channel === 'email'
                    ? `Upload the filled spreadsheet for ${displayTemplateName}. Ensure recipient emails are valid.`
                    : `Upload the filled spreadsheet for ${displayTemplateName}. Use country-code phones such as +9198...`}
                </p>

                <FormField label="Upload file" required>
                  <div className="relative border border-[#D0D5D1] rounded-[8px] px-4 py-4 flex flex-col items-center justify-center bg-white hover:bg-[#FAFAFA] transition-colors">
                    <input
                      type="file"
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                      onChange={(e) =>
                        handleFileChange(e.target.files?.[0] || null)
                      }
                    />
                    <div className="flex flex-col items-center gap-1.5 text-center pointer-events-none">
                      <div className="w-8 h-8 rounded-[6px] bg-[#EBF4EA] flex items-center justify-center text-primary">
                        <FileDocIcon className="w-4 h-4" />
                      </div>
                      {file ? (
                        <span className="text-[14px] font-medium text-text-primary">
                          {file.name}
                        </span>
                      ) : (
                        <>
                          <p className="text-[14px] leading-[18px]">
                            <span className="font-semibold text-primary">
                              Click to upload
                            </span>{' '}
                            <span className="text-text-muted">
                              or drag and drop
                            </span>
                          </p>
                          <p className="text-[12px] text-text-muted">
                            XLSX or CSV, max 10 MB
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                </FormField>
              </div>
            )}

            {step === 3 && previewData && (
              <div className="flex flex-col gap-5">
                <p className="text-sm text-text-secondary">
                  Import {validRows} of {totalRows} rows.
                </p>

                <div className="flex flex-col gap-[10px]">
                  <h3 className="text-[18px] leading-[26px] font-medium text-primary font-sf-pro-display">
                    Sending Method
                  </h3>
                  <div className="bg-white rounded-[12px] p-4 flex flex-col gap-4 w-full">
                    <div className="w-full max-w-2xl">
                      <TemplateScheduleOptions
                        ref={scheduleOptionsRef}
                        key={previewData.preview_token}
                        disabled={isConfirming}
                        allowedModes={
                          channel === 'email' || !sendOnContactDate
                            ? ['scheduled', 'interval']
                            : ['yearly_date']
                        }
                        initialMode={
                          channel !== 'whatsapp' || !sendOnContactDate
                            ? standardSendingMode
                            : 'yearly_date'
                        }
                        onModeChange={(mode) => {
                          if (mode === 'scheduled' || mode === 'interval') {
                            setStandardSendingMode(mode);
                          }
                        }}
                        serializeDateTime={formatDateTimeOffset}
                        alwaysShowSharedFields
                        namePlaceholder="e.g., March Payment Reminder"
                        importLabels
                      />
                    </div>
                  </div>
                </div>

                {(isCarousel || isMediaRequired) && (
                  <div className="flex flex-col gap-[10px]">
                    <h3 className="text-[18px] leading-[26px] font-medium text-primary font-sf-pro-display">
                      Template Media
                    </h3>
                    <div className="bg-white rounded-[12px] p-4 flex flex-col gap-4 w-full">
                      <div className="w-full max-w-2xl flex flex-col gap-4">
                        {isCarousel ? (
                          carouselCards.map((card, idx) => {
                            const ci = cardImages[idx];
                            return (
                              <FormField
                                key={idx}
                                label={`Card ${idx + 1} Media`}
                                required
                              >
                                <div className="relative border border-[#D0D5D1] rounded-[8px] px-4 py-4 flex flex-col items-center justify-center bg-white hover:bg-[#FAFAFA] transition-colors">
                                  {!ci && (
                                    <input
                                      type="file"
                                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                      accept=".jpg,.jpeg,.png,.mp4"
                                      onChange={(e) =>
                                        handleCardMediaChange(
                                          idx,
                                          e.target.files?.[0] || null,
                                          card.media_type,
                                        )
                                      }
                                    />
                                  )}
                                  <div className="flex flex-col items-center gap-1.5 text-center">
                                    <div className="w-8 h-8 rounded-[6px] bg-[#EBF4EA] flex items-center justify-center text-primary pointer-events-none">
                                      <FileDocIcon className="w-4 h-4" />
                                    </div>
                                    {ci ? (
                                      <div className="flex flex-col items-center gap-1">
                                        <span className="text-[14px] font-medium text-text-primary truncate max-w-[200px]">
                                          {ci.file.name}
                                        </span>
                                        <button
                                          type="button"
                                          className="text-[12px] text-red-500 hover:underline cursor-pointer z-10 relative"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            handleCardMediaChange(
                                              idx,
                                              null,
                                              card.media_type,
                                            );
                                          }}
                                        >
                                          Remove
                                        </button>
                                      </div>
                                    ) : (
                                      <div className="pointer-events-none">
                                        <p className="text-[14px] leading-[18px]">
                                          <span className="font-semibold text-primary">
                                            Click to upload
                                          </span>{' '}
                                          <span className="text-text-muted">
                                            or drag and drop
                                          </span>
                                        </p>
                                        <p className="text-[12px] text-text-muted">
                                          {card.media_type === 'VIDEO'
                                            ? 'H.264 MP4 (max 16 MB)'
                                            : 'Images (max 15 MB)'}
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </FormField>
                            );
                          })
                        ) : (
                          <FormField label="Header Media" required>
                            <div className="relative border border-[#D0D5D1] rounded-[8px] px-4 py-4 flex flex-col items-center justify-center bg-white hover:bg-[#FAFAFA] transition-colors">
                              {!mediaFile && (
                                <input
                                  type="file"
                                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                  accept=".jpg,.jpeg,.png,.mp4,.pdf"
                                  onChange={(e) =>
                                    handleMediaChange(
                                      e.target.files?.[0] || null,
                                    )
                                  }
                                />
                              )}
                              <div className="flex flex-col items-center gap-1.5 text-center">
                                <div className="w-8 h-8 rounded-[6px] bg-[#EBF4EA] flex items-center justify-center text-primary pointer-events-none">
                                  <FileDocIcon className="w-4 h-4" />
                                </div>
                                {mediaFile ? (
                                  <div className="flex flex-col items-center gap-1">
                                    <span className="text-[14px] font-medium text-text-primary truncate max-w-[200px]">
                                      {mediaFile.name}
                                    </span>
                                    <button
                                      type="button"
                                      className="text-[12px] text-red-500 hover:underline cursor-pointer z-10 relative"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleMediaChange(null);
                                      }}
                                    >
                                      Remove
                                    </button>
                                  </div>
                                ) : (
                                  <div className="pointer-events-none">
                                    <p className="text-[14px] leading-[18px]">
                                      <span className="font-semibold text-primary">
                                        Click to upload
                                      </span>{' '}
                                      <span className="text-text-muted">
                                        or drag and drop
                                      </span>
                                    </p>
                                    <p className="text-[12px] text-text-muted">
                                      Images, Videos, or Documents (max 15 MB)
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </FormField>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="px-4 sm:px-6 py-4 sm:py-5 lg:pl-0 shrink-0 lg:h-full lg:overflow-y-auto custom-scrollbar flex-col hidden lg:flex">
            {channel === 'email' ? (
              <ImportEmailPreviewPanel
                template={phonePreviewTemplate}
                subject={customSubject || schema?.subject || ''}
              />
            ) : (
              <ImportPhonePreviewPanel
                template={phonePreviewTemplate}
                loading={Boolean(templateId) && isSchemaLoading}
              />
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={previewOpen && !!previewData}
        onClose={closePreviewModal}
        title="Import preview"
        size="2xl"
        panelClassName="!flex !flex-col !overflow-hidden"
        bodyClassName="min-h-0 flex-1 overflow-y-auto custom-scrollbar"
        footer={
          <>
            <Button variant="secondary" onClick={closePreviewModal}>
              Fix file
            </Button>
            <span className="relative inline-flex">
              {previewData?.valid_rows_count &&
              hasDuplicateConflicts(previewData) &&
              (!duplicateAction ||
                (needsSkipContactPick(duplicateAction) &&
                  selectedDuplicateKeys.length === 0)) ? (
                <button
                  type="button"
                  className="absolute inset-0 z-10 cursor-not-allowed"
                  onClick={handlePreviewContinue}
                  aria-label={
                    !duplicateAction
                      ? CONFLICT_RESOLUTION_REQUIRED_COPY
                      : CONFLICT_CONTACT_REQUIRED_COPY
                  }
                />
              ) : null}
              <Button
                onClick={handlePreviewContinue}
                disabled={
                  !previewData?.valid_rows_count ||
                  (hasDuplicateConflicts(previewData) &&
                    (!duplicateAction ||
                      (needsSkipContactPick(duplicateAction) &&
                        selectedDuplicateKeys.length === 0)))
                }
              >
                Continue
              </Button>
            </span>
          </>
        }
      >
        {previewData && (
          <ReminderImportPreviewStep
            previewData={previewData}
            duplicateAction={duplicateAction}
            onDuplicateActionChange={handleDuplicateActionChange}
            duplicateActionError={duplicateActionError}
            selectedDuplicateKeys={selectedDuplicateKeys}
            onToggleDuplicateRow={handleToggleDuplicateRow}
            onToggleAllDuplicates={handleToggleAllDuplicates}
            duplicateContactError={duplicateContactError}
            missingAction={missingAction}
            onMissingActionChange={setMissingAction}
            channel={channel}
          />
        )}
      </Modal>
    </DashboardPage>
  );
}
